'use client'

import { Box, Center, Flex } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useMutation } from '@urql/next'
import type React from 'react'
import { useContext, useEffect, useState, useCallback } from 'react'

import { IdentityContext, PeerAccountContext, StyledButton, useAppAuth } from '~/app/_components'
import { AcceptOrCreateFriendship, DeclineFriendship } from '~/app/_graphql'
import { type Account, AccountType, ConnectionState } from '~/generated/graphql'
import { appTrace } from '~/utils'

type AccountWithConnectStatus = Pick<Account, 'id' | 'connectionStatus' | 'type' | 'profile'>

const ConnectButtonTexts = {
  connect: 'CONNECT',
  connected: 'CONNECTED',
  sent: 'INVITE SENT',
  received: 'INVITE RECEIVED',
  addFolio: 'ADD FOLIO TO CONNECT',
} as const

type ButtonSize = 'small' | 'medium' | 'large'
type ButtonType = 'primary' | 'passive' | 'secondary'
type ButtonLayout = 'row' | 'column'

type ButtonPreset = {
  size: ButtonSize
  type: ButtonType
  layout?: ButtonLayout
  fontSize?: string
}

const buttonPresets = {
  profile: {
    size: 'small' as const,
    type: 'passive' as const,
    fontSize: '13px',
  },
  business: {
    size: 'medium' as const,
    type: 'primary' as const,
    layout: 'row' as const,
  },
  agency: {
    size: 'medium' as const,
    type: 'primary' as const,
    layout: 'row' as const,
  },
  notifications: {
    size: 'medium' as const,
    type: 'primary' as const,
    layout: 'row' as const,
  },
  businessProfile: {
    size: 'medium' as const,
    type: 'primary' as const,
    layout: 'row' as const,
  },
  desktopAgency: {
    size: 'medium' as const,
    type: 'primary' as const,
    layout: 'row' as const,
  },
  mobileAgency: {
    size: 'medium' as const,
    type: 'primary' as const,
    layout: 'row' as const,
  },
  community: {
    size: 'medium' as const,
    type: 'passive' as const,
    layout: 'row' as const,
  },
} as const

interface ConnectButtonProps {
  preset?: keyof typeof buttonPresets
  defaultHide?: boolean
  business?: boolean
  customProps?: Partial<ButtonPreset>
}

interface BaseButtonProps extends ButtonPreset {
  text: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  isDisabled?: boolean
  testId?: string
}

function getButtonText(account: AccountWithConnectStatus, business = false, isHidden = false): string {
  if (isHidden) return ConnectButtonTexts.addFolio
  if (account.connectionStatus === ConnectionState.Connected) return ConnectButtonTexts.connected
  if (account.connectionStatus === ConnectionState.Sent) return ConnectButtonTexts.sent
  if (account.type !== AccountType.Personal && business) return ConnectButtonTexts.connect
  return ConnectButtonTexts.connect
}

function BaseButton({ text, onClick, isDisabled = false, testId, size, type, fontSize }: BaseButtonProps) {
  const isSmall = size === 'small'
  const isMedium = size === 'medium'

  return (
    <StyledButton
      testId={testId}
      fz={fontSize}
      type={type}
      text={text}
      onClick={onClick}
      disablePadding={isSmall}
      noPadding={isSmall}
      width="100%"
      isDisabled={isDisabled}
      highHeight={isMedium}
      slimHeight={isSmall}
      size={size}
    />
  )
}

function ActOnInviteButtons({ size, fontSize, preset }: ButtonPreset & { preset: keyof typeof buttonPresets }) {
  if (!preset) {
    throw new Error('Preset is required for ActOnInviteButtons')
  }
  const { account } = useContext(PeerAccountContext)
  const [, executeUpdateAccept] = useMutation(AcceptOrCreateFriendship)
  const [, executeUpdateDecline] = useMutation(DeclineFriendship)

  const handleAcceptFriendshipRequest = useCallback(
    async (e?: React.MouseEvent<HTMLButtonElement>) => {
      appTrace('handleAcceptFriendshipRequest', account?.id)
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      if (!account?.id) throw new Error('Account is required for ActOnInviteButtons')

      const result = await executeUpdateAccept({
        recipientId: account.id,
      })

      if (result.data?.acceptOrCreateFriendship?.__typename === 'Account') {
        notifications.show({
          title: 'Invite Accepted',
          message: 'The user is now added to your contact list.',
        })
      } else if (result?.data?.acceptOrCreateFriendship?.__typename === 'Error') {
        notifications.show({
          title: 'Something went wrong',
          message: result.data.acceptOrCreateFriendship.message,
          color: 'warning',
        })
      }
    },
    [account?.id, executeUpdateAccept]
  )

  const handleDeclineFriendshipRequest = useCallback(
    async (e?: React.MouseEvent<HTMLButtonElement>) => {
      appTrace('handleDeclineFriendshipRequest', account?.id)
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      if (!account?.id) throw new Error('Account is required for ActOnInviteButtons')

      const result = await executeUpdateDecline({
        requesterId: account.id,
      })

      if (result.data?.declineFriendship?.__typename === 'FriendshipWithAccount') {
        notifications.show({
          title: 'Invite Declined',
          message: 'The user can still appear in your result feed.',
        })
      } else if (result?.data?.declineFriendship?.__typename === 'Error') {
        notifications.show({
          title: 'Something went wrong',
          message: result.data.declineFriendship.message,
          color: 'red',
        })
      }
    },
    [account?.id, executeUpdateDecline]
  )

  return (
    <Flex gap="sm" w="100%" justify="center">
      <BaseButton
        type="primary"
        text="ACCEPT"
        fontSize={fontSize}
        onClick={handleAcceptFriendshipRequest}
        testId={`${preset}-accept-button`}
        size={size}
      />
      <BaseButton
        type="secondary"
        text="IGNORE"
        fontSize={fontSize}
        onClick={handleDeclineFriendshipRequest}
        testId={`${preset}-decline-button`}
        size={size}
      />
    </Flex>
  )
}

export function ConnectButton({ preset = 'profile', defaultHide = false, business, customProps }: ConnectButtonProps) {
  const { account } = useContext(PeerAccountContext)
  const { isSignedIn, handleSignIn } = useAppAuth()
  const { currentAccount } = useContext(IdentityContext)
  const [node, setNode] = useState<React.ReactNode>(null)

  const buttonProps = {
    ...buttonPresets[preset],
    ...customProps,
  }

  const handleSignInWithReturn = () => {
    localStorage.setItem('redirect_to', window.location.pathname)
    handleSignIn()
  }

  const openModal = () => {
    const minWidth = window.innerWidth < 768 ? '90vw' : '600px'
    modals.openContextModal({
      modal: 'connect',
      title: `INVITE ${account.profile?.name?.toUpperCase()} TO CONNECT.`,
      innerProps: { account },
      styles: { title: { fontSize: '24px', fontWeight: '700' }, content: { minWidth, borderRadius: '25px' } },
      withCloseButton: false,
      centered: true,
    })
  }

  const isDisabled =
    account.connectionStatus === ConnectionState.Sent || account.connectionStatus === ConnectionState.Connected

  const notConnected = account.connectionStatus === ConnectionState.NotConnected

  useEffect(() => {
    const renderNode = () => {
      if (defaultHide) return null

      const text = getButtonText(account, business, currentAccount?.isHidden ?? false)

      if (!isSignedIn) {
        return <BaseButton text={text} onClick={handleSignInWithReturn} {...buttonProps} />
      }

      if (account.connectionStatus === ConnectionState.Received) {
        if (!preset) {
          throw new Error('Preset is required for ConnectButton when showing ActOnInviteButtons')
        }
        return <ActOnInviteButtons {...buttonProps} preset={preset} />
      }

      return (
        <BaseButton
          text={text}
          isDisabled={isDisabled || (currentAccount?.isHidden ?? false)}
          onClick={notConnected ? openModal : undefined}
          testId={`connect-button-${account.id}`}
          {...buttonProps}
        />
      )
    }

    setNode(renderNode())
  }, [isDisabled, notConnected])

  if (defaultHide) return null

  return (
    <Center w="100%">
      <Box w="100%">
        <Flex
          justify={buttonProps.layout === 'row' ? 'flex-end' : 'center'}
          gap="sm"
          w="100%"
          direction={buttonProps.layout}
        >
          {node}
        </Flex>
      </Box>
    </Center>
  )
}
